import React, { FC, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    EditorialNav,
    EditorialFooter,
    Eyebrow,
    QUIZ_PATH,
    mono,
    serif,
    hairline,
    hair2,
    ivory,
    bodyText,
    muted,
    subtle,
} from './landing/EditorialShell';
import { startPolarCheckout } from '../lib/polar';
import sendAnalyticsEvent from '../utils/sendAnalyticsEvent';
import { useT } from '../i18n/useT';
import '../assets/scss/landing-v2.scss';

interface Plan {
    id: 'single';
    name: string;
    price: number;
    pricePerWeek: number;
    badge?: string;
    accent: string;
    cta: string;
    features: string[];
    popular?: boolean;
}

const PLANS: Plan[] = [
    {
        id: 'single',
        name: 'Full Report + Protocol',
        price: 19.99,
        pricePerWeek: 4.99,
        badge: '7-DAY FREE TRIAL',
        accent: '#ef4444',
        cta: 'START FREE TRIAL',
        features: [
            'Built on 50 years of research: CBT, Behavioral Activation, ACT, behavioral economics, social psychology',
            'Full 25-question diagnostic',
            '5-dimension functional profile',
            '20-blocker patterns',
            '10 growth patterns',
            'Personalized action protocol - 240 exercises',
            'Cancel anytime during your trial',
        ],
        popular: true,
    },
];

const FAQS: { q: string; a: string }[] = [
    {
        q: "What's included in every plan?",
        a: 'Every plan includes the full 25-question behavioral diagnostic, your profile across 5 dimensions, primary blocker identification with scientific explanation, and a personalized action protocol you can start the same day.',
    },
    {
        q: 'What makes Pro different from Regular?',
        a: 'Pro includes a deep-dive into all five dimensions with multi-framework comparison and weekly progress tracking. You get a more detailed understanding of how your patterns interact across areas.',
    },
    {
        q: 'What happens in mentor sessions?',
        a: 'Master plan includes 2 one-on-one sessions with experienced behavioral coaches who help you interpret your profile, identify blind spots, and create a personalized 90-day roadmap tailored to your specific blockers.',
    },
    {
        q: 'Can I upgrade my plan later?',
        a: 'Yes. You can upgrade to a higher tier at any time by paying the difference. Contact our support team to arrange an upgrade.',
    },
    {
        q: 'How long does the diagnostic take?',
        a: 'About 8–10 minutes. Your full development profile and personalized protocol are ready the moment you finish - no waiting.',
    },
    {
        q: 'Do you offer refunds?',
        a: 'Yes - 7-day no-questions guarantee. If the result feels inaccurate or unhelpful, just send us a message at support@doyourstep.com. No forms, no hoops.',
    },
];

const PricingPage: FC = () => {
    const navigate = useNavigate();
    const t = useT();
    const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

    const handlePayment = async (plan: Plan) => {
        setLoadingPlan(plan.id);
        try {
            sendAnalyticsEvent({
                eventName: 'payment_initiated_from_pricing',
                data: {
                    plan_id: plan.id,
                    plan_name: plan.name,
                    price: plan.price,
                    source: 'pricing_page',
                },
            });

            await startPolarCheckout({
                returnPath: '/subscription-success/direct-purchase',
                metadata: { planId: plan.id, planName: plan.name, source: 'pricing_page' },
            });
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Payment error:', err);
            // eslint-disable-next-line no-alert
            window.alert('Failed to initialize payment. Please try again.');
            setLoadingPlan(null);
        }
    };

    return (
        <div
            className="lp2"
            style={{
                background: '#0a0a0a',
                color: ivory,
                fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, sans-serif",
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <EditorialNav />
            <main style={{ flex: 1 }}>
                {/* Hero */}
                <section
                    style={{
                        padding: 'clamp(56px, 9vw, 120px) clamp(20px, 4vw, 40px) clamp(48px, 7vw, 80px)',
                        textAlign: 'center',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: '40%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 1400,
                            height: 700,
                            pointerEvents: 'none',
                            background: 'radial-gradient(ellipse at 50% 50%, #ef444422 0%, transparent 65%)',
                        }}
                    />
                    <div style={{ maxWidth: 880, margin: '0 auto', position: 'relative' }}>
                        <Eyebrow color="#ef4444">{t('pricing.hero.eyebrow')}</Eyebrow>
                        <h1
                            className="lp2__h2"
                            style={{
                                fontSize: 'clamp(36px, 6vw, 72px)',
                                fontWeight: 200,
                                color: ivory,
                                letterSpacing: '-0.04em',
                                lineHeight: 1.05,
                                margin: '14px 0 18px',
                            }}
                        >
                            {t('pricing.hero.title.lead')}{' '}
                            <span
                                style={{
                                    color: muted,
                                    fontStyle: 'italic',
                                    fontFamily: serif,
                                    fontWeight: 300,
                                }}
                            >
                                {t('pricing.hero.title.italic')}
                            </span>
                        </h1>
                        <p
                            style={{
                                fontSize: 17,
                                color: bodyText,
                                lineHeight: 1.6,
                                margin: '0 auto',
                                maxWidth: 640,
                            }}
                        >
                            {t('pricing.hero.subtitle')}
                        </p>
                    </div>
                </section>

                {/* Pricing cards */}
                <section
                    style={{
                        padding: '0 clamp(20px, 4vw, 40px) clamp(64px, 9vw, 120px)',
                    }}
                >
                    <div
                        className="lp2__plans"
                        style={{
                            maxWidth: 440,
                            margin: '0 auto',
                            display: 'grid',
                            gridTemplateColumns: '1fr',
                            gap: 16,
                            alignItems: 'stretch',
                        }}
                    >
                        {PLANS.map((plan) => {
                            const isLoadingThis = loadingPlan === plan.id;
                            const weekly = plan.pricePerWeek.toFixed(2);

                            return (
                                <div
                                    key={plan.id}
                                    className={plan.popular ? 'lp2__plan lp2__plan--popular' : 'lp2__plan'}
                                    style={{
                                        background: plan.popular ? '#100b0b' : '#0c0b0a',
                                        border: plan.popular
                                            ? `1px solid ${plan.accent}55`
                                            : hairline,
                                        borderRadius: 6,
                                        padding: '40px 32px 36px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        position: 'relative',
                                        boxShadow: plan.popular
                                            ? `0 0 60px ${plan.accent}22`
                                            : 'none',
                                    }}
                                >
                                    {plan.badge && (
                                        <span
                                            style={{
                                                position: 'absolute',
                                                top: 16,
                                                right: 16,
                                                fontSize: 9,
                                                color: plan.accent,
                                                background: `${plan.accent}14`,
                                                border: `1px solid ${plan.accent}40`,
                                                padding: '4px 10px',
                                                borderRadius: 999,
                                                fontFamily: mono,
                                                letterSpacing: '0.16em',
                                                fontWeight: 600,
                                            }}
                                        >
                                            {plan.badge}
                                        </span>
                                    )}

                                    <h3
                                        style={{
                                            fontSize: 13,
                                            color: muted,
                                            letterSpacing: '0.22em',
                                            fontFamily: mono,
                                            fontWeight: 500,
                                            margin: '0 0 18px',
                                            textTransform: 'uppercase',
                                        }}
                                    >
                                        {plan.name}
                                    </h3>

                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 4,
                                            marginBottom: 4,
                                            minHeight: 80,
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontSize: 24,
                                                color: muted,
                                                fontFamily: mono,
                                                marginTop: 14,
                                            }}
                                        >
                                            $
                                        </span>
                                        <span
                                            style={{
                                                fontSize: 80,
                                                fontWeight: 200,
                                                color: ivory,
                                                letterSpacing: '-0.04em',
                                                lineHeight: 1,
                                                fontFamily: mono,
                                            }}
                                        >
                                            {weekly}
                                        </span>
                                        <span
                                            style={{
                                                fontSize: 20,
                                                color: muted,
                                                fontFamily: mono,
                                                alignSelf: 'flex-end',
                                                marginBottom: 12,
                                            }}
                                        >
                                            /week
                                        </span>
                                    </div>
                                    <p
                                        style={{
                                            fontSize: 11,
                                            color: subtle,
                                            margin: '0 0 28px',
                                            fontFamily: mono,
                                            letterSpacing: '0.18em',
                                            textTransform: 'uppercase',
                                        }}
                                    >
                                        {`7 DAYS FREE · THEN $${plan.price}/MO, BILLED MONTHLY · CANCEL ANYTIME`}
                                    </p>

                                    <div
                                        style={{
                                            height: 1,
                                            background: 'rgba(232, 230, 224, 0.06)',
                                            marginBottom: 24,
                                        }}
                                    />

                                    <ul
                                        style={{
                                            margin: '0 0 32px',
                                            padding: 0,
                                            listStyle: 'none',
                                            flex: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 10,
                                        }}
                                    >
                                        {plan.features.map((f) => (
                                            <li
                                                key={f}
                                                style={{
                                                    position: 'relative',
                                                    paddingLeft: 22,
                                                    fontSize: 14,
                                                    color: bodyText,
                                                    lineHeight: 1.5,
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        position: 'absolute',
                                                        left: 0,
                                                        top: 11,
                                                        width: 12,
                                                        height: 1,
                                                        background: plan.accent,
                                                    }}
                                                />
                                                {f}
                                            </li>
                                        ))}
                                    </ul>

                                    <button
                                        type="button"
                                        onClick={() => handlePayment(plan)}
                                        disabled={loadingPlan !== null}
                                        style={{
                                            background: plan.popular ? plan.accent : 'transparent',
                                            color: plan.popular ? ivory : ivory,
                                            border: plan.popular ? 'none' : `1px solid ${plan.accent}55`,
                                            padding: '14px 24px',
                                            borderRadius: 4,
                                            fontSize: 13,
                                            fontWeight: 600,
                                            cursor: loadingPlan !== null ? 'not-allowed' : 'pointer',
                                            fontFamily: mono,
                                            letterSpacing: '0.1em',
                                            opacity: loadingPlan !== null && !isLoadingThis ? 0.5 : 1,
                                            boxShadow: plan.popular
                                                ? `0 0 32px ${plan.accent}40`
                                                : 'none',
                                            marginBottom: 12,
                                        }}
                                    >
                                        {isLoadingThis ? 'LOADING…' : `${plan.cta} →`}
                                    </button>

                                    <Link
                                        to={QUIZ_PATH}
                                        style={{
                                            fontSize: 11,
                                            color: muted,
                                            textAlign: 'center',
                                            textDecoration: 'none',
                                            fontFamily: mono,
                                            letterSpacing: '0.08em',
                                        }}
                                    >
                                        Or start the diagnostic first →
                                    </Link>
                                </div>
                            );
                        })}
                    </div>
                </section>

                {/* FAQ */}
                <section
                    style={{
                        padding: 'clamp(64px, 9vw, 120px) clamp(20px, 4vw, 40px)',
                        borderTop: hairline,
                        background: '#08070790',
                    }}
                >
                    <div style={{ maxWidth: 1080, margin: '0 auto' }}>
                        <Eyebrow>QUESTIONS ABOUT PLANS</Eyebrow>
                        <h2
                            className="lp2__h2"
                            style={{
                                fontSize: 'clamp(28px, 4.5vw, 48px)',
                                fontWeight: 300,
                                color: ivory,
                                letterSpacing: '-0.025em',
                                lineHeight: 1.1,
                                margin: '14px 0 56px',
                            }}
                        >
                            Common questions.{' '}
                            <span
                                style={{
                                    color: muted,
                                    fontStyle: 'italic',
                                    fontFamily: serif,
                                    fontWeight: 300,
                                }}
                            >
                                Direct answers.
                            </span>
                        </h2>

                        <div
                            className="lp2__faq-grid"
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: '0 56px',
                            }}
                        >
                            {FAQS.map((f, i) => (
                                <div
                                    key={f.q}
                                    style={{
                                        padding: '24px 0',
                                        borderTop: i === 0 || i === 1 ? hair2 : hair2,
                                    }}
                                >
                                    <h3
                                        style={{
                                            fontSize: 17,
                                            fontWeight: 500,
                                            color: ivory,
                                            margin: '0 0 10px',
                                            letterSpacing: '-0.01em',
                                        }}
                                    >
                                        {f.q}
                                    </h3>
                                    <p
                                        style={{
                                            fontSize: 14,
                                            color: bodyText,
                                            lineHeight: 1.65,
                                            margin: 0,
                                        }}
                                    >
                                        {f.a}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Final CTA */}
                <section
                    style={{
                        padding: 'clamp(80px, 12vw, 160px) clamp(20px, 4vw, 40px)',
                        borderTop: hairline,
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 1400,
                            height: 800,
                            pointerEvents: 'none',
                            background:
                                'radial-gradient(ellipse at 50% 50%, #ef444422 0%, transparent 60%)',
                        }}
                    />
                    <div
                        style={{
                            maxWidth: 920,
                            margin: '0 auto',
                            textAlign: 'center',
                            position: 'relative',
                        }}
                    >
                        <Eyebrow color="#ef4444">{t('pricing.cta.final.eyebrow')}</Eyebrow>
                        <h2
                            className="lp2__cta-title"
                            style={{
                                fontSize: 'clamp(36px, 7vw, 72px)',
                                fontWeight: 200,
                                margin: '20px 0 24px',
                                letterSpacing: '-0.04em',
                                lineHeight: 1.02,
                                color: ivory,
                            }}
                        >
                            {t('pricing.cta.final.title.lead')}{' '}
                            <span
                                style={{
                                    fontStyle: 'italic',
                                    fontFamily: serif,
                                    color: '#ef4444',
                                    fontWeight: 300,
                                }}
                            >
                                {t('pricing.cta.final.title.italic')}
                            </span>
                        </h2>
                        <p
                            style={{
                                fontSize: 17,
                                color: bodyText,
                                lineHeight: 1.6,
                                margin: '0 auto 36px',
                                maxWidth: 580,
                            }}
                        >
                            {t('pricing.cta.final.body')}
                        </p>
                        <button
                            type="button"
                            onClick={() => navigate(QUIZ_PATH)}
                            style={{
                                background: '#ef4444',
                                color: ivory,
                                border: 'none',
                                padding: '16px 36px',
                                borderRadius: 4,
                                fontSize: 14,
                                fontWeight: 600,
                                cursor: 'pointer',
                                fontFamily: mono,
                                letterSpacing: '0.12em',
                                boxShadow: '0 0 36px rgba(239, 68, 68, 0.45)',
                            }}
                        >
                            {t('pricing.cta.final.button')}
                        </button>
                    </div>
                </section>
            </main>
            <EditorialFooter />
        </div>
    );
};

export default PricingPage;
